import { ERROR_STATUS } from '../../../../constants/api';
import { t } from '../../../../helpers/language';
import { EZLOGIC_TITLE_SOMETHING_WENT_WRONG } from '../../../../constants/language_tokens';
import mainTypes from '../../../../constants/ActionTypes/Main';
import { sleep } from '../../../../helpers/sleep';

/**
 * Handles query response errors
 * @param {Object} response - query response
 * @returns {undefined} In case of an error, it will create new Error and throw it out
 * @example
 * responseErrorHandling({data: { data: { error_text: 'Error'}}})
 * */
export const responseErrorHandling = (response) => {
    if (response.data.status === ERROR_STATUS) {
        const message =
            response.data.data?.error_text ||
            response.data.data?.error_message ||
            t(EZLOGIC_TITLE_SOMETHING_WENT_WRONG);
        throw new Error(message);
    }
};

/**
 * Custom error class representing an Ezlo-specific error.
 *
 * @class EzloError
 * @extends {Error}
 * @param {string} message - The error message.
 * @param {string} type - The type of the Ezlo error.
 */
export class EzloError extends Error {
    constructor(message, type) {
        super(message);
        this.type = type;
    }
}

/**
 * Default success check for Cloud API responses: the body carries `status === SUCCESS_STATUS`.
 *
 * @param {Object} response - Raw response returned by `fn`.
 * @returns {boolean} Whether the response should be treated as a success.
 */
const isCloudStatusSuccess = (response) => response?.data?.status === mainTypes.SUCCESS_STATUS;

/**
 * Retries an async call until it succeeds or the attempt budget runs out.
 *
 * A response is considered a success when `isSuccess(response)` is truthy; otherwise (or on a thrown
 * error) the call is retried after `delay` ms until `limit` attempts are exhausted. The default
 * `isSuccess` matches Cloud API responses (`status === SUCCESS_STATUS`); pass a custom predicate for
 * endpoints whose body has a different success shape (e.g. an account REST endpoint without `status`).
 *
 * @param {() => Promise<Object>} fn - Function performing the request; should resolve to the raw response.
 * @param {Object} [options]
 * @param {number} [options.limit] - Remaining attempts.
 * @param {number} [options.delay] - Delay between attempts, in ms.
 * @param {(response: Object) => boolean} [options.isSuccess] - Success predicate for the response.
 * @returns {Promise<Object>} The successful response.
 */
export const retry = (
    fn,
    { limit = mainTypes.DEFAULT_LIMIT_COUNT, delay = mainTypes.DEFAULT_TIMEOUT, isSuccess = isCloudStatusSuccess } = {},
) => {
    return fn()
        .then((response) => {
            if (!isSuccess(response)) {
                throw response;
            }

            return response;
        })
        .catch(async (err) => {
            const shouldRetry = limit > mainTypes.MIN_LIMIT_COUNT;
            if (!shouldRetry) {
                throw err;
            }

            await sleep(delay);

            return retry(fn, { limit: limit - mainTypes.STEP_DECREASING_LIMIT, delay, isSuccess });
        });
};
